import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;

const PDF_URL =
  'https://storage.readdy-site.link/project_files/7fd54c6e-5bf9-4b97-9111-4d2e51964b66/3d8d089f-17a5-4e7b-a594-28f9a192e5fe_roblox-secrets--top-50-hidden-secrets-you-shouldnt-see--unmlx.pdf?v=dff6d86d17f6a20d4c83a21c36d4cb67';

export default function SuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    // Verify order in DB
    const verifyOrder = async () => {
      try {
        const res = await fetch(`${SUPABASE_URL}/functions/v1/verify-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        const data = await res.json();
        if (data.email) {
          setEmail(data.email);
          setStatus('success');
        } else {
          // Still show success — Stripe confirmed the payment
          setStatus('success');
        }
      } catch {
        // Fallback: show success anyway since Stripe redirected here
        setStatus('success');
      }
    };

    verifyOrder();
  }, [sessionId]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-16"
      style={{ background: 'linear-gradient(135deg, #0a0a0f 0%, #0d0d1a 50%, #0a0a0f 100%)' }}
    >
      {/* Background glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(ellipse, #7c3aed 0%, transparent 70%)', filter: 'blur(80px)' }}
        />
      </div>

      <div className="relative z-10 max-w-lg w-full text-center">
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4">
            <div
              className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-purple-500/50"
              style={{ background: 'rgba(168,85,247,0.1)' }}
            >
              <i className="ri-loader-4-line text-purple-400 text-4xl animate-spin"></i>
            </div>
            <p className="text-gray-400 text-lg">Verifying your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div
            className="rounded-3xl border border-purple-500/40 p-8 md:p-12"
            style={{ background: 'linear-gradient(160deg, #1a1a2e 0%, #0f0f1a 100%)' }}
          >
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div
                className="w-24 h-24 flex items-center justify-center rounded-full border-2 border-yellow-400/60"
                style={{
                  background: 'rgba(250,204,21,0.1)',
                  boxShadow: '0 0 40px rgba(250,204,21,0.4)',
                }}
              >
                <i className="ri-checkbox-circle-line text-yellow-400 text-5xl"></i>
              </div>
            </div>

            <h1
              className="font-black text-white text-3xl md:text-4xl mb-3"
              style={{ textShadow: '0 0 30px rgba(250,204,21,0.5)', fontFamily: "'Orbitron', sans-serif" }}
            >
              Secret Unlocked! 🎮
            </h1>

            <p className="text-gray-400 text-base mb-2">
              Payment confirmed! Your guide is ready.
            </p>

            {email && (
              <p className="text-purple-400 text-sm mb-8">
                A download link has also been sent to <strong className="text-purple-300">{email}</strong>
              </p>
            )}

            {!email && (
              <p className="text-gray-500 text-sm mb-8">
                Check your email — we sent you a download link too!
              </p>
            )}

            {/* Download button */}
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-yellow-400 text-black font-black uppercase tracking-wider px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 hover:bg-yellow-300 hover:scale-105 text-base mb-6"
              style={{ boxShadow: '0 0 30px rgba(250,204,21,0.5)' }}
            >
              <i className="ri-download-2-line text-xl"></i>
              Download Your Guide
            </a>

            {/* What's inside */}
            <div
              className="rounded-2xl border border-purple-800/40 p-5 mb-8 text-left"
              style={{ background: 'rgba(168,85,247,0.06)' }}
            >
              <p className="text-yellow-400 font-black text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                <i className="ri-eye-line"></i>
                What&apos;s Inside Your Guide
              </p>
              {[
                '50 hidden Roblox secrets revealed',
                'Secret rooms & hidden locations',
                'Pro tricks & glitches',
                'Step-by-step instructions',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-gray-400 text-sm py-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-checkbox-circle-fill text-yellow-500 text-sm"></i>
                  </div>
                  {item}
                </div>
              ))}
            </div>

            {/* Back home */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-300 text-sm transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Roblox Secrets
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div
            className="rounded-3xl border border-red-500/30 p-8 md:p-12"
            style={{ background: 'linear-gradient(160deg, #1a1a2e 0%, #0f0f1a 100%)' }}
          >
            <div className="flex justify-center mb-6">
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-red-500/50"
                style={{ background: 'rgba(239,68,68,0.1)' }}
              >
                <i className="ri-error-warning-line text-red-400 text-4xl"></i>
              </div>
            </div>
            <h1 className="font-black text-white text-2xl mb-3" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              Something went wrong
            </h1>
            <p className="text-gray-400 text-sm mb-6">
              If you completed payment, check your email for the download link. Otherwise contact us.
            </p>
            <a
              href="mailto:thekids@robloxsecrets.pro"
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm transition-colors"
            >
              <i className="ri-mail-line"></i>
              thekids@robloxsecrets.pro
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
